// OrphanDefaultView — admin escolhe qual organização (company) recebe
// alunos sem vínculo a outra organização. Backend já lê o setting de
// `platform_config["orphan_default_company_slug"]`; aqui só damos UI.
//
// [[project-admin-default-orphan-org]] [[feedback-orphan-users-to-geral]]

import { useEffect, useState } from "preact/hooks";
import {
  Building2,
  CheckCircle2,
  Loader2,
  Save,
  ShieldQuestion
} from "lucide-react";
import { api } from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { openAlert } from "./ConfirmPromptModal";

type OrgRow = { id: string; slug: string; name: string };

export function OrphanDefaultView() {
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [current, setCurrent] = useState<OrgRow | null>(null);
  const [options, setOptions] = useState<OrgRow[]>([]);
  const [draftSlug, setDraftSlug] = useState<string>("");
  const [saving, setSaving] = useState(false);

  async function load() {
    setPhase("loading");
    setErrMsg(null);
    try {
      const r = await api.getOrphanDefault();
      setCurrent(r.current);
      setOptions(r.options);
      setDraftSlug(r.current?.slug ?? "geral");
      setPhase("ready");
    } catch (e) {
      setErrMsg(friendlyError(e));
      setPhase("error");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function handleSave() {
    if (!draftSlug.trim()) return;
    setSaving(true);
    try {
      const r = await api.setOrphanDefault(draftSlug.trim());
      setCurrent(r.current);
      void openAlert({
        title: "Salvo",
        message: `Novos alunos sem organização agora vão para "${r.current.name}".`
      });
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setSaving(false);
    }
  }

  const isDirty = draftSlug.trim() && draftSlug.trim() !== current?.slug;

  if (phase === "loading") {
    return (
      <div class="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando configuração…
      </div>
    );
  }
  if (phase === "error") {
    return (
      <div class="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
        {errMsg ?? "Falha ao carregar."}
      </div>
    );
  }

  return (
    <div class="space-y-6">
      <div class="rounded-md border border-violet-500/30 bg-violet-500/5 p-4">
        <div class="flex items-start gap-3">
          <ShieldQuestion className="mt-0.5 size-5 shrink-0 text-violet-600" />
          <div class="space-y-1 text-sm">
            <h3 class="font-bold">Organização default para órfãos</h3>
            <p class="text-muted-foreground">
              Quando uma pessoa se cadastra (ou faz login via Google) sem
              estar atrelada a nenhuma organização específica, ela recebe
              automaticamente uma membership de <strong>aluno</strong> nesta
              organização. Útil para que ela já consiga acessar bancos
              públicos, comprar pacotes e ver conteúdo da plataforma desde
              o primeiro login.
            </p>
            <p class="text-muted-foreground">
              O padrão é a organização com slug <code>geral</code>. Se você
              trocar para uma organização específica, novos órfãos serão
              colocados <strong>lá</strong>. <em>Alunos já existentes não
              são realocados</em>.
            </p>
          </div>
        </div>
      </div>

      <section class="space-y-3 rounded-md border border-border bg-card p-5">
        <header class="flex items-center gap-2">
          <Building2 className="size-4 text-primary" />
          <h2 class="text-sm font-bold">Organização atual</h2>
        </header>
        {current ? (
          <div class="inline-flex items-center gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/5 px-3 py-2 text-sm">
            <CheckCircle2 className="size-4 text-emerald-600" />
            <span class="font-bold">{current.name}</span>
            <span class="font-mono text-[11px] text-muted-foreground">
              ({current.slug})
            </span>
          </div>
        ) : (
          <div class="rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
            Nenhuma organização default configurada. Órfãos podem ficar sem
            membership até que você selecione uma abaixo.
          </div>
        )}
      </section>

      <section class="space-y-3 rounded-md border border-border bg-card p-5">
        <header>
          <h2 class="text-sm font-bold">Trocar para outra organização</h2>
          <p class="mt-1 text-xs text-muted-foreground">
            Selecione a organização que deve receber novos cadastros sem
            vínculo. A mudança vale a partir do próximo cadastro.
          </p>
        </header>
        <label class="block">
          <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Organização
          </span>
          <select
            value={draftSlug}
            onChange={(e) =>
              setDraftSlug((e.target as HTMLSelectElement).value)
            }
            class="w-full max-w-md rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
          >
            {options.map((o) => (
              <option key={o.id} value={o.slug}>
                {o.name} ({o.slug})
              </option>
            ))}
          </select>
        </label>
        <div class="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || saving}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Save className="size-3.5" />
            )}
            {saving ? "Salvando…" : "Salvar"}
          </button>
          {isDirty && (
            <button
              type="button"
              onClick={() => setDraftSlug(current?.slug ?? "geral")}
              disabled={saving}
              class="text-xs font-medium text-muted-foreground hover:text-foreground hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancelar
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
